import {
  ChangeDetectionStrategy, Component, EventEmitter, Input, Output, AfterViewInit, ElementRef, ChangeDetectorRef
} from '@angular/core';
import isEmpty from 'lodash-es/isEmpty';

import { ScrollFormService } from '@reservation/services/scroll-form.service';
import { SwipeType } from '@ux/entities/swipe-types.enum';
import { DropDownViewCloseAction, DropDownViewCloseType } from '@ux/components/drop-down-view/entities/drop-down-view.types';
import { TypographyFontSize, TypographyColor } from '@design-system/components/atoms/typography/entities/typography.types';
import { InputSearchAction } from '@design-system/components/molecules/input-search/entities/input-search.types';

@Component({
  selector: 'app-input-filter',
  templateUrl: './input-filter.component.html',
  styleUrls: ['./input-filter.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class InputFilterComponent implements AfterViewInit {

  @Input() label: string;
  @Input() placeholder: string;
  @Input() labelBySearch: string;
  @Input() options: string[] = [];
  @Input() isMobile: boolean;
  @Input() isOpenBox: boolean = false;
  @Input() index: number = -1;
  @Input() delay: number = 500;
  @Input() set value(value: string) {
    this._value = value;
  }

  @Output() toggleIsOpenBox: EventEmitter<boolean> = new EventEmitter();
  @Output() changeHotelName: EventEmitter<string> = new EventEmitter<string>();
  @Output() swipeUpInput: EventEmitter<boolean> = new EventEmitter();

  public readonly filterSelector: string = 'filter-';
  public readonly inputFilterSelector: string = '-input';
  public readonly closeAction = DropDownViewCloseAction.Back;
  public readonly closeType = DropDownViewCloseType.Route;
  
  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;
  
  public _filterValues: string[] = [];
  
  public showSwipeUp: boolean = false;
  public swipeStyle: SwipeType = SwipeType.FULL;

  private _value = '';
  private _selected = null;

  constructor(
    private readonly scrollFormService: ScrollFormService,
    private readonly cdr: ChangeDetectorRef,
  ) { }

  ngAfterViewInit(): void {
    this._filterValues = this.options;
    this._selected = this._value;
    this.cdr.detectChanges();
  }

  get value(): string {
    return this._value;
  }

  get showTextSearch(): boolean {
    return this._filterValues.length < this.options.length;
  }

  get showClearIconInFilters(): boolean {
    return !!this.value;
  }

  get inputSearchAction(): InputSearchAction {
    return this.isMobile ? InputSearchAction.Typified : InputSearchAction.Button;
  }

  public focusInput(elementRef: ElementRef): void {
    if (this.isMobile && !this.showSwipeUp) {
      this.toggleInputfilter();
    } else if (!this.isMobile) {
      this.scrollFormService.scrollIntoView(elementRef);
    }
  }

  public toggleOpenBox(): void {
    this.toggleIsOpenBox.emit(!this.isOpenBox);
  }

  public toggleInputfilter(): void {
    if (this.isMobile) {
      this.showSwipeUp = !this.showSwipeUp;
      this.swipeUpInput.emit(this.showSwipeUp);
    }
  }

  public clearField(): void {
    this._value = '';
    this._filterValues = this.options;
    if (!isEmpty(this._selected)) {
      this.changeHotelName.emit('');
      this._selected = null;
    }
    this.cdr.detectChanges();
  }

  public onChangeInput(value: string): void {
    if (!isEmpty(value)) {
      this._value = value;
      if (this.isMobile) {
        this._filterValues = this.filterByValue(this.options, value);
      }
    } else {
      this.clearField();
    }
  }

  public filterByValue(array: string[], value: string): string[] {
    const filterNames = [];
    array.forEach((val) => {
      const indexOf = val.toLowerCase().indexOf(value.toLowerCase());
      if (indexOf !== -1) {
        const inital = val.slice(0, indexOf);
        const final = val.slice(indexOf + value.length, val.length);
        filterNames.push(`${inital}<b>${val.slice(indexOf, indexOf + value.length)}</b>${final}`);
      }
    });
    return !value || value.trim() === '' ? this.options : filterNames;
  }

  public selectHotelName(value: string): void {
    if (!isEmpty(value)) {
      this._value = this.removeBoldHtml(value);
      this._selected = this.removeBoldHtml(value);
      this.changeHotelName.emit(this.removeBoldHtml(value));
      this.toggleInputfilter();
      this.cdr.detectChanges();
    } else {
      this.clearField();
    }
  }

  public removeBoldHtml(value: string): string {
    return value?.replace('<b>', '').replace('</b>', '');
  }
}
